import React, { useState, useMemo } from 'react';
import { ConnectionRequestsList } from './ConnectionRequestsList';
import { MyConnectionsList } from './MyConnectionsList';
import { ChevronDown, ChevronUp, X } from 'lucide-react';
import { useConnectionRequests } from '@/hooks/useConnectionRequests';
import { useI18n } from '@/contexts/I18nContext';
import { supabase } from '@/integrations/supabase/client';
import searchIcon from '../../assets/icons/search.svg';

interface ConnectionsTabProps {
  connections: any[];
  refetchConnections: () => Promise<void>;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  fetchNextPage?: () => void;
  noContainer?: boolean;
}

export const ConnectionsTab: React.FC<ConnectionsTabProps> = ({ 
  connections, 
  refetchConnections, 
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  noContainer = false 
}) => {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const { requests, acceptRequest, declineRequest } = useConnectionRequests();
  const { t } = useI18n();

  // Filter connections based on search query
  const filteredConnections = useMemo(() => {
    if (!searchQuery.trim()) return connections;
    
    const query = searchQuery.toLowerCase();
    return connections.filter(connection => 
      connection.full_name?.toLowerCase().includes(query) ||
      connection.username?.toLowerCase().includes(query) ||
      connection.academy?.toLowerCase().includes(query)
    );
  }, [connections, searchQuery]);

  const handleAcceptRequest = async (requesterId: string) => {
    try {
      // Accept the connection directly
      const { error } = await supabase.rpc('accept_connection_request', { requester_id: requesterId });
      if (error) {
        console.error('Error accepting connection:', error);
        return;
      }
      // Close the accordion after accepting the request
      setOpen(false);
      // Immediately update both lists
      await Promise.all([
        acceptRequest(requesterId), // Updates the requests list
        refetchConnections() // Updates the connections list
      ]);
    } catch (error) {
      console.error('Error processing connection acceptance:', error);
    }
  };

  const handleDeclineRequest = async (requesterId: string) => {
    await declineRequest(requesterId);
    // If there are no more requests, close the accordion
    if (requests.length <= 1) {
      setOpen(false);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
  };

  const content = (
    <div className="min-w-[320px]">
      {requests.length > 0 && (
        <>
          <div
            className="rounded-lg mb-6 cursor-pointer flex items-center justify-between py-3 bg-transparent select-none text-sm"
            onClick={() => setOpen((v) => !v)}
            role="button"
            tabIndex={0}
            aria-expanded={open}
          >
            <span className="font-semibold text-white flex items-center gap-2">
              {t('connections.requests')}
              <span className="inline-flex items-center justify-center rounded-full bg-[#D72E16] text-white text-[11px] font-bold min-w-[18px] h-[18px] px-1.5">
                {requests.length}
              </span>
            </span>
            {open ? (
              <ChevronUp className="w-5 h-5 text-white" />
            ) : (
              <ChevronDown className="w-5 h-5 text-white" />
            )}
          </div>
          {open && (
            <div className="mb-8">
              <ConnectionRequestsList 
                onAcceptRequest={handleAcceptRequest}
                onDeclineRequest={handleDeclineRequest}
              />
            </div>
          )}
          <div className="border-b border-white/10 w-full mb-2" />
        </>
      )}
      
      {/* Search field */}
      <div className="relative mb-6">
        <div className="relative">
          <img
            src={searchIcon}
            alt="Search"
            className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white/60 w-5 h-5"
          />
          <input
            type="text"
            placeholder={t('connections.search_connections_placeholder')}
            value={searchQuery}
            onChange={handleSearchChange}
            className="w-full bg-white/5 border border-white rounded-xl py-2.5 pl-12 pr-12 text-white text-[12px] placeholder-white/60 focus:outline-none transition-all duration-200 font-sans"
          />
          {searchQuery && (
            <button
              onClick={handleClearSearch}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white/60 hover:text-white transition-colors duration-200"
              aria-label={t('connections.clear_search')}
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>

      <MyConnectionsList 
        onConnectionChange={refetchConnections} 
        connections={filteredConnections}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        fetchNextPage={fetchNextPage}
        isSearching={searchQuery.trim().length > 0}
      />
    </div>
  );

  if (noContainer) {
    return content;
  }

  return (
    <div className="w-full max-w-2xl mx-auto mt-6">
      {content}
    </div>
  );
}; 